import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allGuideArticles, type GuideArticle } from '@/mocks/seoContent';

export default function ArticlePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const slug = location.pathname;

  const article: GuideArticle | undefined = useMemo(
    () => allGuideArticles.find((a) => a.slug === slug),
    [slug],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://vigorbuy.team';

  const faqSection = article?.sections.find(
    (s) => s.heading.toLowerCase().includes('faq') || s.heading.toLowerCase().includes('frequently asked'),
  );

  const faqPairs: { question: string; answer: string }[] = [];
  if (faqSection) {
    const raw = faqSection.content;
    const segments = raw.split(/(?<=\?)\s+/);
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i].trim();
      if (seg.endsWith('?')) {
        let answer = '';
        for (let j = i + 1; j < segments.length; j++) {
          if (segments[j].trim().endsWith('?')) break;
          answer += (answer ? ' ' : '') + segments[j].trim();
          i = j;
        }
        if (answer) faqPairs.push({ question: seg, answer });
      }
    }
  }

  const jsonLdGraph: Record<string, unknown>[] = [
    {
      '@type': 'BlogPosting',
      headline: article?.title,
      description: article?.metaDescription,
      author: { '@type': 'Organization', name: 'VigorBuy Editorial Team' },
      publisher: { '@type': 'Organization', name: 'VigorBuy.team' },
      datePublished: '2026-06-29',
      dateModified: '2026-06-29',
      image: article?.heroImage,
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}${article?.slug}` },
    },
  ];

  if (faqPairs.length > 0) {
    jsonLdGraph.push({
      '@type': 'FAQPage',
      mainEntity: faqPairs.map((qa) => ({
        '@type': 'Question',
        name: qa.question,
        acceptedAnswer: { '@type': 'Answer', text: qa.answer },
      })),
    });
  }

  const articleJsonLd = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': jsonLdGraph,
  });

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="text-3xl font-bold text-foreground-900">Article Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            Go Back Home
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: articleJsonLd }}
      />
      <Navbar />

      <article className="pt-20">
        <header className="relative w-full bg-background-100">
          <div className="w-full px-4 md:px-6 py-12 md:py-20">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
              <div className="lg:w-3/5">
                <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap">
                  <Link to="/" className="hover:text-primary-600 transition-colors duration-150 whitespace-nowrap cursor-pointer">Home</Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <span className="text-foreground-600 whitespace-nowrap">Shopping Guide</span>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </span>
                  <span className="text-foreground-900 font-medium truncate">{article.tag}</span>
                </nav>

                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wide mb-4">
                  {article.tag}
                </span>

                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-900 leading-tight italic">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-foreground-500">
                  <span className="font-medium text-foreground-700">VigorBuy Editorial Team</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>Last Updated June 2026</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-xs"></i>
                    </span>
                    {article.readingTime}
                  </span>
                </div>
              </div>

              <div className="lg:w-2/5">
                <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden">
                  <img
                    src={article.heroImage}
                    alt={article.heroImageAlt}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="w-full py-12 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                <p className="text-lg text-foreground-700 leading-relaxed mb-10">
                  {article.articleIntro}
                </p>

                {article.sections.map((section, idx) => (
                  <div key={idx} className="mb-10">
                    <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 italic mb-5">
                      {section.heading}
                    </h2>
                    <div className="text-base md:text-lg text-foreground-700 leading-relaxed space-y-5">
                      {section.content.split('\n').filter(Boolean).map((para, pIdx) => (
                        <p key={pIdx}>{para.trim()}</p>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-lg border border-background-200/70">
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-900 italic mb-4">
                    Final Thoughts
                  </h2>
                  <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                    {article.conclusion}
                  </p>
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-6 bg-accent-100/50 rounded-lg border border-accent-200/50">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center">
                      <i className="ri-information-line text-background-50 text-lg"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Editorial Note</p>
                      <p className="text-sm text-foreground-600">Content reviewed and updated by the VigorBuy Editorial Team. Last review: June 2026.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200">
                <Link
                  to="/"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-sm"></i>
                  </span>
                  Back to VigorBuy Spreadsheet main page
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h3 className="font-heading text-xl md:text-2xl text-foreground-900 italic mb-6">
                More Shopping Guides
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {allGuideArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 4)
                  .map((a) => (
                    <div key={a.slug} className="p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-foreground-300 transition-all duration-200">
                      <span className="text-xs text-foreground-400 uppercase tracking-wider">{a.tag}</span>
                      <h4 className="mt-1.5 text-sm font-semibold text-foreground-900 line-clamp-2">{a.title}</h4>
                      <Link
                        to={a.slug}
                        className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        Read More
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                      </Link>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </main>
  );
}